import { useEffect, type FunctionComponent } from 'react';
import { Autocomplete, AutocompleteProps } from '@mantine/core';
import { ProviderSearchLocationValues } from '@/components/ProviderSearch/ProviderSearchForm/types';
import { useDebouncedCallback, useUncontrolled } from '@mantine/hooks';
import useMapboxSearchApi from '@/hooks/useMapboxSearchApi';
import Loader from '@/components/ds/Loader';

export interface LocationAutocompleteProps extends Omit<
  AutocompleteProps,
  'defaultValue' | 'value' | 'onChange'
> {
  value?: ProviderSearchLocationValues;
  defaultValue?: ProviderSearchLocationValues;
  onChange?: (value: ProviderSearchLocationValues) => void;
}

const cleanFullAddress = (fullAddress: string) =>
  fullAddress.replace(', United States', '');

const LocationAutocomplete: FunctionComponent<LocationAutocompleteProps> = ({
  value,
  defaultValue,
  onChange,
  ...inputParams
}) => {
  const [_value, setValue] = useUncontrolled({
    value,
    defaultValue,
    finalValue: {
      text: '',
      latitude: undefined,
      longitude: undefined,
    },
    onChange,
  });

  const [fetchGeoAutocomplete, { data: suggestions, loading, reset }] =
    useMapboxSearchApi(
      'geoCodingForward',
      {
        country: 'us',
        types: 'postcode,locality,place',
        language: 'en',
        limit: 5,
      },
      {
        onCompleted: (data) => {
          const match = data.features.find(
            ({ properties }) =>
              cleanFullAddress(properties.full_address) === _value.text
          );

          if (match) {
            setValue({
              text: cleanFullAddress(match.properties.full_address),
              latitude: match.properties.coordinates.latitude,
              longitude: match.properties.coordinates.longitude,
            });
          } else {
            setValue({
              text: _value.text,
            });
          }
        },
      }
    );

  const debouncedApiRequest = useDebouncedCallback((value: string) => {
    fetchGeoAutocomplete({ q: value });
  }, 500);

  const handleSearchChange = (value: string) => {
    if (!value) {
      reset();
      setValue({
        text: '',
        latitude: undefined,
        longitude: undefined,
      });
      return;
    }

    // If unchanged, don't override the lat/lon
    if (_value.text === value) {
      return;
    }

    setValue({
      text: value,
    });
  };

  useEffect(() => {
    if (_value.text) {
      debouncedApiRequest(_value.text);
    } else {
      reset();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps
     -- trigger API completion when the text changes */
  }, [_value.text]);

  const formattedSuggestions =
    suggestions?.features.map(({ id, properties }) => ({
      label: cleanFullAddress(properties.full_address),
      value: id,
    })) ?? [];

  return (
    <Autocomplete
      {...inputParams}
      rightSection={loading ? <Loader meaning="accent" size="xs" /> : null}
      value={_value.text}
      data={formattedSuggestions}
      onChange={handleSearchChange}
      clearable={true}
    />
  );
};

export default LocationAutocomplete;
